/* eslint-disable react/prop-types */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Row, Select, Col } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';

import WorkflowActionNode from './WorkflowActionNode';
import WorkflowHandle from './WorkflowHandle';
import OnTraccrNumberInput from '../../common/inputs/OnTraccrNumberInput';
import { getType, updateData } from './workflowHelpers';

const HOVER_TEXT = 'Pause the workflow for a configured amount of time before downstream steps run automatically.';

const DELAY_TIME_OPTS = [
  { value: 'hours', label: 'Hours' },
  { value: 'days', label: 'Days' },
];

const ANCHOR_OPTS = [
  { value: 'workflowReached', label: 'When workflow reaches this step' },
  { value: 'formCreated', label: 'Form creation date' },
  { value: 'formSubmitted', label: 'Form submission date' },
  { value: 'dateField', label: 'Date field from form' },
];

const getErrorDetails = ({
  delayAnchor,
  delayOffset,
  delayDateFieldId,
}) => {
  const amount = delayOffset?.amount;
  const time = delayOffset?.time;
  const hasValidOffset = amount != null && amount > 0 && time;

  if (!hasValidOffset) {
    return {
      content: (
        <div style={{ maxWidth: 300 }}>
          This step requires a valid <b>delay duration</b>.
        </div>
      ),
    };
  }

  if (delayAnchor === 'dateField' && !delayDateFieldId) {
    return {
      content: (
        <div style={{ maxWidth: 300 }}>
          This step requires a <b>date field</b> when using a form date anchor.
        </div>
      ),
    };
  }

  return null;
};

export default function WorkflowTimeDelayNode({
  setElements,
  setDataMap,
  isDisplay,
  sections = [],
  setDisableNext,
} = {}) {
  // eslint-disable-next-line react/function-component-definition
  return function WorkflowTimeDelayNodeComponent({
    id,
    draggable,
    data = {},
  }) {
    const {
      delayAnchor: initialDelayAnchor = 'workflowReached',
      delayOffset: initialDelayOffset = { amount: 1, time: 'days' },
      delayDateFieldId: initialDelayDateFieldId = null,
    } = data;

    const [delayAnchor, setDelayAnchor] = useState(initialDelayAnchor);
    const [delayOffset, setDelayOffset] = useState(initialDelayOffset);
    const [delayDateFieldId, setDelayDateFieldId] = useState(initialDelayDateFieldId);

    const dateFieldOptions = useMemo(() => {
      const fields = [];
      sections.forEach(({ fields: sectionFields = [], name }) => {
        sectionFields?.forEach((field) => {
          const type = getType(field);
          if (type === 'dateTime') {
            fields.push({
              value: field.id,
              label: `${name}: ${field.configProps.title}`,
            });
          }
        });
      });
      return fields;
    }, [sections]);

    const isInvalid = useMemo(() => {
      const amount = delayOffset?.amount;
      const time = delayOffset?.time;
      const hasValidOffset = amount != null && amount > 0 && time;
      if (!hasValidOffset) return true;
      if (delayAnchor === 'dateField' && !delayDateFieldId) return true;
      return false;
    }, [delayAnchor, delayDateFieldId, delayOffset]);

    useEffect(() => {
      if (!setDisableNext) return;
      setDisableNext((prev) => ({ ...prev, [id]: isInvalid }));
    }, [id, isInvalid, setDisableNext]);

    const persistData = useCallback((updates) => {
      if (!setDataMap) return;
      setDataMap(updateData(id, {
        delayAnchor,
        delayOffset,
        delayDateFieldId,
        ...updates,
      }));
    }, [delayAnchor, delayDateFieldId, delayOffset, id, setDataMap]);

    const onAnchorChange = useCallback((value) => {
      setDelayAnchor(value);
      const nextDateFieldId = value === 'dateField' ? delayDateFieldId : null;
      if (value !== 'dateField') {
        setDelayDateFieldId(null);
      }
      persistData({
        delayAnchor: value,
        delayDateFieldId: nextDateFieldId,
      });
    }, [delayDateFieldId, persistData]);

    const onAmountChange = useCallback((value) => {
      const nextOffset = { ...delayOffset, amount: value };
      setDelayOffset(nextOffset);
      persistData({ delayOffset: nextOffset });
    }, [delayOffset, persistData]);

    const onTimeChange = useCallback((value) => {
      const nextOffset = { ...delayOffset, time: value };
      setDelayOffset(nextOffset);
      persistData({ delayOffset: nextOffset });
    }, [delayOffset, persistData]);

    const onDateFieldChange = useCallback((value) => {
      setDelayDateFieldId(value);
      persistData({ delayDateFieldId: value });
    }, [persistData]);

    return (
      <WorkflowActionNode
        id={id}
        title="Time Delay"
        Icon={ClockCircleOutlined}
        type="timeDelay"
        draggable={draggable}
        onNodeUpdate={(...args) => {
          setElements(...args);
          if (setDisableNext) {
            setDisableNext((prev) => ({ ...prev, [id]: false }));
          }
        }}
        isDisplay={isDisplay}
        hover={HOVER_TEXT}
        errorPopover={isInvalid ? getErrorDetails({
          delayAnchor,
          delayOffset,
          delayDateFieldId,
        }) : null}
      >
        {!draggable && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Row style={{ marginTop: 5, marginBottom: 8 }}>
                Start delay from:
              </Row>
              <Row>
                <Select
                  style={{ width: '100%' }}
                  options={ANCHOR_OPTS}
                  value={delayAnchor}
                  onChange={onAnchorChange}
                />
              </Row>
            </div>
            {delayAnchor === 'dateField' && (
              <div style={{ marginBottom: 16 }}>
                <Row style={{ marginBottom: 8 }}>
                  Date field:
                </Row>
                <Row>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="Select date field"
                    options={dateFieldOptions}
                    value={delayDateFieldId}
                    onChange={onDateFieldChange}
                  />
                </Row>
              </div>
            )}
            <div style={{ marginBottom: 16 }}>
              <Row style={{ marginBottom: 8 }}>
                Delay duration:
              </Row>
              <Row align="middle" gutter={12} style={{ width: '100%' }}>
                <Col
                  style={{
                    height: 32,
                    width: 75,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <OnTraccrNumberInput
                    min={1}
                    value={delayOffset?.amount}
                    onChange={onAmountChange}
                    style={{ width: '100%' }}
                  />
                </Col>
                <Col style={{ height: 32, display: 'flex', alignItems: 'center' }}>
                  <Select
                    style={{ width: 120 }}
                    options={DELAY_TIME_OPTS}
                    value={delayOffset?.time}
                    onSelect={onTimeChange}
                  />
                </Col>
              </Row>
            </div>
            <WorkflowHandle type="target" position="top" disabled={isDisplay} />
            <WorkflowHandle type="source" position="bottom" disabled={isDisplay} />
          </div>
        )}
      </WorkflowActionNode>
    );
  };
}
